import { Reducer } from 'redux'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import type {
  CollateralTabId,
  PartnerIQPageData,
} from '../../../mocks/partneriq/mockData'
import { partnerIQPageDataEmpty } from '../../../mocks/partneriq/mockData'
import { PartnerIQActionTypes } from './action'

type CustomData = {
  title: string
  value?: number
  contactId?: string
}

export type OverviewComponentData = {
  title: string
  type: CustomData[]
}

export type SegmentsData = {
  segments: string[]
}

export interface ValueData {
  count: number
  value?: number
}

export interface TopPerformer {
  contactId: string
  contactName: string
  ranking: number
  championScore: number
  segment: string
  location: string
}

export interface TopPerformingLocations {
  value: string
}

export interface TopPerformingSegments {
  value: string
}

export interface Opportunities {
  inProgress: ValueData
  closedWon: ValueData
  closedLost: ValueData
}

export interface PartnerIqOverviewResponse {
  topPerformersSegments: TopPerformingSegments[]
  topPerformersLocations: TopPerformingLocations[]
  topPerformers: TopPerformer[]
  opportunities: Opportunities
}
export interface PartnerIQRow {
  id: string
  contactName: string
  title: string
  location: string
  segment: string
  avgIcpFit?: number
  inProgress: ValueData
  closedWon: ValueData
  closedLost: ValueData
  lastActivityOn: string
  ranking: number
  championScore: number
  championBadge: string
}

export type PartnerIqTableData = {
  contacts: PartnerIQRow[] | null
  totalCount: number
}

export interface ICPData {
  id?: string
  icpName: string
  partnerId?: string
  annualRevenue?: string[]
  industry?: string[]
  employeeSize?: string[]
  segment?: string[]
  cloudTechnologyStack?: string[]
  geographicalRegion?: string[]
  marketplacePresence?: string[]
  userId?: string
  description?: string
  createdAt?: string
  updatedAt?: string
  isActive?: boolean
  isDefault?: boolean
}

export interface EngagedWithContact {
  email: string
  name: string
}

export interface ContactPersonalInfo {
  contactProfileIcon: string | null
  contactEmail: string
  contactName: string
  title: string
  crmLink: string
  ranking: number
  linkedinProfileUrl: string
  location: string
  phoneNumber: string
  segment: string
  industries: string[]
  lastActivityOn: string
  engagedWith: EngagedWithContact[]
  opportunityStatusSummary: Opportunities
  collabEnabled?: boolean
}
export interface OpportunityTableRow {
  id: string
  accountName: string
  opportunityName: string
  status: string
  icpFit: number
  ptbScore: string
  lastActivityDate: string
  crmLink: string
}

export interface ProfileData {
  contactPersonalInfo: ContactPersonalInfo
  opportunityTable: OpportunityTableRow[]
}

export type ICPFormField = FormField & { gridSize: { xs: number } }

export type ICPFormMode = 'add' | 'edit'

/** Admin Panel / Collab Room page section data (company info, trusted by, etc.) */
export type AdminPanelPageData = PartnerIQPageData

/** Ensure page data always has all sections with safe shapes so section components never throw. */
function normalizeAdminPanelPageData(
  payload: AdminPanelPageData | null
): AdminPanelPageData | null {
  if (payload == null) return null
  const d = partnerIQPageDataEmpty
  const collateral =
    payload.collateral && typeof payload.collateral === 'object'
      ? {
          tabs: payload.collateral.tabs ?? d.collateral.tabs,
          byTab: payload.collateral.byTab ?? d.collateral.byTab,
          sectionHidden: payload.collateral.sectionHidden,
          firstCallSectionHidden: payload.collateral.firstCallSectionHidden,
          solutionBriefsSectionHidden:
            payload.collateral.solutionBriefsSectionHidden,
          winWiresSectionHidden: payload.collateral.winWiresSectionHidden,
        }
      : d.collateral
  return {
    companyInfo: payload.companyInfo ?? d.companyInfo,
    trustedBy: payload.trustedBy ?? d.trustedBy,
    highlightMetrics: payload.highlightMetrics ?? d.highlightMetrics,
    allianceAndSales: Array.isArray(payload.allianceAndSales)
      ? payload.allianceAndSales
      : d.allianceAndSales,
    teamsVisibility: payload.teamsVisibility,
    collateral,
    solutions: Array.isArray(payload.solutions)
      ? payload.solutions
      : d.solutions,
    solutionsSectionHidden: payload.solutionsSectionHidden,
  }
}

/** When company name changes in admin UI, keep highlights + team headers aligned (same as fetch merge). */
export function syncAllianceSalesTeamTitlesToCompanyName(
  teams: PartnerIQPageData['allianceAndSales'],
  companyName: string
): PartnerIQPageData['allianceAndSales'] {
  const cn = String(companyName ?? '').trim()
  if (!cn || !Array.isArray(teams)) return teams
  return teams.map(team => {
    const t = String(team.teamName ?? '').trim()
    const lower = t.toLowerCase()
    if (lower === 'alliance team' || lower.endsWith(' alliance team')) {
      return { ...team, teamName: `${cn} alliance team` }
    }
    if (lower === 'sales team' || lower.endsWith(' sales team')) {
      return { ...team, teamName: `${cn} sales team` }
    }
    return team
  })
}

/** UI-only state for Admin Panel edit mode and section modal */
export type AdminPanelUIState = {
  isEditMode: boolean
  editingSectionId: string | null
  formValue: string
  /** Collateral list tab — drives which tab opens in the collateral edit modal */
  collateralActiveTabId: CollateralTabId
}

export type PartnerIQState = {
  overviewData: OverviewComponentData[] | null
  tableData: PartnerIqTableData
  profileData: ProfileData | null
  icpFormData: ICPData | null
  segmentsData: SegmentsData | null
  adminPanelPageData: AdminPanelPageData | null
  adminPanelUI: AdminPanelUIState
}

const initialAdminPanelUI: AdminPanelUIState = {
  isEditMode: false,
  editingSectionId: null,
  formValue: '',
  collateralActiveTabId: 'first-call-deck',
}

export const initialState: PartnerIQState = {
  overviewData: null,
  tableData: {
    contacts: null,
    totalCount: 0,
  },
  profileData: null,
  icpFormData: null,
  segmentsData: null,
  adminPanelPageData: null,
  adminPanelUI: initialAdminPanelUI,
}

type actionType =
  | {
      type: PartnerIQActionTypes.SET_PARTNER_IQ_TABLE_DATA
      payload: PartnerIqTableData
    }
  | {
      type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_TABLE_DATA
    }
  | {
      type: PartnerIQActionTypes.SET_PARTNER_IQ_OVERVIEW_DATA
      payload: OverviewComponentData[]
    }
  | {
      type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_PROFILE_DATA
    }
  | {
      type: PartnerIQActionTypes.SET_PARTNER_IQ_PROFILE_DATA
      payload: ProfileData
    }
  | {
      type: PartnerIQActionTypes.SET_PARTNER_IQ_ICP_FORM_DATA
      payload: ICPData | null
    }
  | {
      type: PartnerIQActionTypes.SET_PARTNER_IQ_SEGMENTS_DATA
      payload: SegmentsData
    }
  | {
      type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_SEGMENTS_DATA
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_PAGE_DATA
      payload: AdminPanelPageData
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_SECTION
      payload: {
        section: keyof AdminPanelPageData
        value: AdminPanelPageData[keyof AdminPanelPageData]
      }
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_EDIT_MODE
      payload: boolean
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_EDITING_SECTION
      payload: string | null
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_FORM_VALUE
      payload: string
    }
  | {
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_COLLATERAL_ACTIVE_TAB
      payload: CollateralTabId
    }
  | {
      type: PartnerIQActionTypes.CLEAR_ADMIN_PANEL
    }

export const reducer: Reducer<PartnerIQState, actionType> = (
  _state = initialState,
  action
) => {
  // Ensure adminPanelUI always exists (guards against stale HMR state)
  const state: PartnerIQState = _state.adminPanelUI
    ? _state
    : {
        ..._state,
        adminPanelPageData: _state.adminPanelPageData ?? null,
        adminPanelUI: initialAdminPanelUI,
      }
  switch (action.type) {
    case PartnerIQActionTypes.SET_PARTNER_IQ_TABLE_DATA: {
      return {
        ...state,
        tableData: action.payload,
      }
    }
    case PartnerIQActionTypes.CLEAR_PARTNER_IQ_TABLE_DATA: {
      return {
        ...state,
        tableData: initialState.tableData,
      }
    }
    case PartnerIQActionTypes.SET_PARTNER_IQ_OVERVIEW_DATA: {
      return {
        ...state,
        overviewData: action.payload,
      }
    }
    case PartnerIQActionTypes.SET_PARTNER_IQ_PROFILE_DATA: {
      return {
        ...state,
        profileData: action.payload,
      }
    }
    case PartnerIQActionTypes.CLEAR_PARTNER_IQ_PROFILE_DATA: {
      return {
        ...state,
        profileData: initialState.profileData,
      }
    }
    case PartnerIQActionTypes.SET_PARTNER_IQ_ICP_FORM_DATA: {
      return {
        ...state,
        icpFormData: action.payload,
      }
    }
    case PartnerIQActionTypes.SET_PARTNER_IQ_SEGMENTS_DATA: {
      return {
        ...state,
        segmentsData: action.payload,
      }
    }
    case PartnerIQActionTypes.CLEAR_PARTNER_IQ_SEGMENTS_DATA: {
      return {
        ...state,
        segmentsData: initialState.segmentsData,
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_PAGE_DATA: {
      return {
        ...state,
        adminPanelPageData: normalizeAdminPanelPageData(action.payload),
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_SECTION: {
      const { section, value } = action.payload
      const prev = state.adminPanelPageData
      if (!prev) return state
      if (section === 'companyInfo') {
        const companyInfo = value as AdminPanelPageData['companyInfo']
        const cn = String(companyInfo?.companyName ?? '').trim()
        const next: AdminPanelPageData = {
          ...prev,
          companyInfo,
        }
        if (cn) {
          next.highlightMetrics = {
            ...prev.highlightMetrics,
            companyName: cn,
          }
          next.allianceAndSales = syncAllianceSalesTeamTitlesToCompanyName(
            prev.allianceAndSales,
            cn
          )
        }
        return {
          ...state,
          adminPanelPageData: normalizeAdminPanelPageData(next),
        }
      }
      return {
        ...state,
        adminPanelPageData: {
          ...prev,
          [section]: value,
        },
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_EDIT_MODE: {
      return {
        ...state,
        adminPanelUI: {
          ...state.adminPanelUI,
          isEditMode: action.payload,
        },
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_EDITING_SECTION: {
      return {
        ...state,
        adminPanelUI: {
          ...state.adminPanelUI,
          editingSectionId: action.payload,
          ...(action.payload === null ? { formValue: '' } : {}),
        },
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_FORM_VALUE: {
      return {
        ...state,
        adminPanelUI: {
          ...state.adminPanelUI,
          formValue: action.payload,
        },
      }
    }
    case PartnerIQActionTypes.SET_ADMIN_PANEL_COLLATERAL_ACTIVE_TAB: {
      return {
        ...state,
        adminPanelUI: {
          ...state.adminPanelUI,
          collateralActiveTabId: action.payload,
        },
      }
    }
    case PartnerIQActionTypes.CLEAR_ADMIN_PANEL: {
      return {
        ...state,
        adminPanelPageData: initialState.adminPanelPageData,
        adminPanelUI: initialAdminPanelUI,
      }
    }
    default:
      return state
  }
}
